import { Dispatch, FC, SetStateAction, useEffect, useState } from "react";
import Drawer from "react-modern-drawer";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  ButtonVariant,
  ButtonSize,
  Show,
  IconButton,
  IconButtonSize,
  IconButtonVariant
} from "shared/components";
import { XClose } from "untitledui-js-base";
import { Loader } from "shared/components/Loader";
import queryString from "qs";
import { ApplicationFilters } from "../../index";
import { instance } from "../../../../../api/apiBase";
import { FilterSection } from "./FilterSection";

interface Props {
  filters?: ApplicationFilters;
  setFilters: Dispatch<SetStateAction<ApplicationFilters | undefined>>;
  showFilters: boolean;
  setShowFilters: Dispatch<SetStateAction<boolean>>;
  unitId?: number;
  propertyId?: number;
}

interface FilterOptions {
  cities?: { city: string; count: number }[];
  properties?: { id: string; name: string; count: number }[];
  statuses?: { status: string; count: number }[];
  units?: { id: string; title: string; count: number }[];
}

const QUERY = "/portal/v1/applications/filter_options";

export const FiltersDrawer: FC<Props> = ({
  unitId,
  propertyId,
  filters,
  setFilters,
  setShowFilters,
  showFilters
}) => {
  const queryClient = useQueryClient();
  const { data: filterOptions, isLoading } = useQuery({
    queryKey: [
      "APPLICATION_FILTER_OPTIONS",
      {
        unit_id: unitId,
        property_id: propertyId
      }
    ],
    queryFn: () =>
      instance
        .get(
          QUERY +
            "?" +
            queryString.stringify({
              unit_id: unitId,
              property_id: propertyId
            })
        )
        .then(({ data }: { data: FilterOptions }) => data)
  });

  useEffect(() => {
    if (showFilters) {
      queryClient.invalidateQueries({
        queryKey: [
          "APPLICATION_FILTER_OPTIONS",
          {
            unit_id: unitId,
            property_id: propertyId
          }
        ]
      });
      setSelectedProperties(filters?.properties || []);
      setSelectedCities(filters?.cities || []);
      setSelectedUnits(filters?.units || []);
    }
  }, [showFilters]);

  const [selectedProperties, setSelectedProperties] = useState<
    {
      id: string;
      name: string;
    }[]
  >([]);
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const [selectedUnits, setSelectedUnits] = useState<
    {
      id: string;
      name: string;
    }[]
  >([]);

  const applyFilters = () => {
    setFilters({
      properties: selectedProperties,
      cities: selectedCities,
      units: selectedUnits
    });
    setShowFilters(false);
  };

  return (
    <Drawer
      className="rounded-l-lg"
      direction="right"
      open={showFilters}
      onClose={() => setShowFilters(false)}
      size={360}
    >
      <div className="flex items-center justify-between px-6 py-4 border-b border-alpha-40">
        <div className="flex items-center gap-4">
          <h1 className="text-xl font-bold text-content-1000">Filters</h1>
          <Show if={isLoading}>
            <Loader size={25} />
          </Show>
        </div>
        <IconButton
          variant={IconButtonVariant.BASE}
          size={IconButtonSize.SM}
          borderLess
          className="translate-x-2"
          onClick={() => setShowFilters(false)}
          Icon={XClose}
        />
      </div>

      <div className="px-6">
        <div className="py-4 border-b border-alpha-40">
          <Button
            variant={ButtonVariant.TEXT}
            className="ml-2 text-base font-bold"
            onClick={() => {
              setSelectedProperties([]);
              setSelectedCities([]);
              setSelectedUnits([]);
            }}
          >
            Unselect all
          </Button>
        </div>

        <Show
          if={!Object.values(filterOptions ?? {}).some((opt) => opt?.length)}
        >
          <p className="my-1 text-sm text-content-400">
            {isLoading ? "Loading..." : "No options available"}
          </p>
        </Show>

        <div className="divide-y divide-alpha-40 overflow-y-auto h-[calc(100vh-211px)] hide-scroll">
          <Show if={Boolean(filterOptions?.properties)}>
            <FilterSection
              title="Property"
              options={filterOptions?.properties ?? []}
              optionLimit={5}
              selectedOptions={selectedProperties.map(
                (property) => property.id
              )}
              onChange={(propertyId) => {
                setSelectedProperties((prev) =>
                  prev.some((p) => p.id === propertyId)
                    ? prev.filter((p) => p.id !== propertyId)
                    : [
                        ...prev,
                        {
                          id: propertyId,
                          name:
                            filterOptions?.properties?.find(
                              (p) => p.id === propertyId
                            )?.name ?? ""
                        }
                      ]
                );
              }}
            />
          </Show>
          <Show if={Boolean(filterOptions?.cities)}>
            <FilterSection
              title="City"
              options={
                filterOptions?.cities?.map(({ city, count }) => ({
                  name: city,
                  count
                })) ?? []
              }
              optionLimit={5}
              selectedOptions={selectedCities}
              onChange={(city) =>
                setSelectedCities((prev) =>
                  prev.includes(city)
                    ? prev.filter((c) => c !== city)
                    : [...prev, city]
                )
              }
            />
          </Show>
          <Show if={Boolean(filterOptions?.units)}>
            <FilterSection
              title="Unit"
              options={
                filterOptions?.units?.map(({ id, title, count }) => ({
                  id,
                  name: title,
                  count
                })) ?? []
              }
              optionLimit={5}
              selectedOptions={selectedUnits.map((unit) => unit.id)}
              onChange={(unitId) => {
                setSelectedUnits((prev) =>
                  prev.some((u) => u.id === unitId)
                    ? prev.filter((u) => u.id !== unitId)
                    : [
                        ...prev,
                        {
                          id: unitId,
                          name:
                            filterOptions?.units?.find((u) => u.id === unitId)
                              ?.title ?? ""
                        }
                      ]
                );
              }}
            />
          </Show>
        </div>

        <div className="absolute bottom-0 py-4 flex gap-2 border-t border-alpha-40">
          <Button
            className="w-[152px]"
            size={ButtonSize.BASE}
            onClick={() => setShowFilters(false)}
          >
            Cancel
          </Button>
          <Button
            className="w-[152px]"
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.BASE}
            onClick={applyFilters}
          >
            Apply
          </Button>
        </div>
      </div>
    </Drawer>
  );
};
